import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2, Check, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export const SignupModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !name) {
      toast.error("Por favor completa todos los campos");
      return;
    }

    setIsLoading(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    setIsLoading(false);
    setIsSuccess(true);
    
    toast.success("¡Bienvenido a Antropia!", {
      description: "Revisa tu email para continuar.",
    });

    // Reset after showing success
    setTimeout(() => {
      setIsSuccess(false);
      setEmail("");
      setName("");
      onClose();
    }, 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md bg-[#0a0a0a] border-white/10 p-0 overflow-hidden">
        <AnimatePresence mode="wait">
          {!isSuccess ? (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-6"
            >
              <DialogHeader className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-[#E05D3A]" />
                  <span className="text-xs text-[#E05D3A] font-semibold tracking-wider uppercase">
                    Beta Exclusiva
                  </span>
                </div>
                <DialogTitle className="font-outfit text-2xl font-light text-white">
                  Comienza tu prueba gratuita
                </DialogTitle>
                <p className="text-zinc-500 text-sm mt-2">
                  14 días con acceso completo. Sin tarjeta de crédito.
                </p>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-zinc-400 mb-1.5 block">
                    Tu nombre
                  </label>
                  <Input
                    type="text"
                    placeholder="Dr. María García"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E05D3A]/50 focus:ring-[#E05D3A]/20 h-12"
                    data-testid="signup-name-input"
                  />
                </div>

                <div>
                  <label className="text-sm text-zinc-400 mb-1.5 block">
                    Tu email profesional
                  </label>
                  <Input
                    type="email"
                    placeholder="maria@clinica.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus:border-[#E05D3A]/50 focus:ring-[#E05D3A]/20 h-12"
                    data-testid="signup-email-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#E05D3A] text-white hover:bg-[#C85233] transition-all duration-300 shadow-[0_0_20px_rgba(224,93,58,0.3)] hover:shadow-[0_0_30px_rgba(224,93,58,0.5)] rounded-full px-6 py-4 text-base font-medium flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="signup-submit-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Creando tu cuenta...
                    </>
                  ) : (
                    <>
                      Comenzar prueba gratuita
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>

              <p className="text-center text-zinc-600 text-xs mt-6">
                Al registrarte aceptas nuestros{" "}
                <a href="/terminos-y-condiciones" className="text-[#E05D3A] hover:underline">
                  Términos
                </a>{" "}
                y{" "}
                <a href="/politica-privacidad" className="text-[#E05D3A] hover:underline">
                  Política de Privacidad
                </a>
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="p-8 text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="w-16 h-16 bg-[#E05D3A]/20 rounded-full flex items-center justify-center mx-auto mb-4"
              >
                <Check className="w-8 h-8 text-[#E05D3A]" />
              </motion.div>
              <h3 className="font-outfit text-xl text-white mb-2">
                ¡Bienvenido a Antropia!
              </h3>
              <p className="text-zinc-500 text-sm">
                Revisa tu email para activar tu cuenta.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};
